'use client'

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

export type AccentVar = '--sky' | '--yellow' | '--acid' | '--hot' | '--mauve'

type AccentEntry = { v: AccentVar; label: string; weight: number }

const ACCENTS: AccentEntry[] = [
  { v: '--sky', label: 'sky', weight: 3 },
  { v: '--yellow', label: 'yellow', weight: 1 },
  { v: '--acid', label: 'acid', weight: 1 },
  { v: '--hot', label: 'hot', weight: 1 },
  { v: '--mauve', label: 'mauve', weight: 1 }
]

function pickAccent(): AccentEntry {
  const pool = ACCENTS.flatMap((a) => Array(a.weight).fill(a) as AccentEntry[])
  return pool[Math.floor(Math.random() * pool.length)]
}

function applyAccentVar(v: AccentVar) {
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--accent', `var(${v})`)
  }
}

type AccentContextValue = {
  v: AccentVar
  label: string
  reroll: () => void
}

const AccentContext = createContext<AccentContextValue>({
  v: '--sky',
  label: 'sky',
  reroll: () => {}
})

interface AccentProviderProps {
  children: ReactNode
  /** Pins the accent to this value and skips session randomization entirely — for
   * environments with no real session (e.g. Remotion renders) that need a fixed,
   * repeatable accent instead of the app's per-visitor random pick. */
  initialAccent?: AccentVar
}

export function AccentProvider({ children, initialAccent }: AccentProviderProps) {
  // Render the same default on server and during the first client render so
  // hydration is byte-identical. Upgrade to the stored / random accent in an
  // effect after mount — hydrating a random value here historically caused
  // attribute mismatches that crashed iOS WebKit during reconciliation.
  const pinned = initialAccent ? ACCENTS.find((a) => a.v === initialAccent) : undefined
  const [accent, setAccent] = useState<AccentEntry>(pinned ?? ACCENTS[0])

  // One-shot upgrade from the SSR-safe default to a random/persisted accent
  // after mount, so server and first client render stay byte-identical.
  useEffect(() => {
    if (pinned) return
    let next: AccentEntry
    try {
      const stored = sessionStorage.getItem('st-accent') as AccentVar | null
      const hit = stored ? ACCENTS.find((a) => a.v === stored) : undefined
      if (hit) {
        next = hit
      } else {
        next = pickAccent()
        sessionStorage.setItem('st-accent', next.v)
      }
    } catch {
      next = pickAccent()
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional post-hydration upgrade
    setAccent(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `pinned` intentionally excluded: this effect must run at most once per mount
  }, [])

  useEffect(() => {
    applyAccentVar(accent.v)
  }, [accent.v])

  const reroll = useCallback(() => {
    let next = pickAccent()
    let g = 0
    while (next.v === accent.v && g++ < 6) next = pickAccent()
    sessionStorage.setItem('st-accent', next.v)
    setAccent(next)
  }, [accent])

  return (
    <AccentContext.Provider value={{ v: accent.v, label: accent.label, reroll }}>{children}</AccentContext.Provider>
  )
}

export function useAccent(): AccentContextValue {
  return useContext(AccentContext)
}
